

import React from 'react';
import { PieChartSegment } from '../types';

interface PieChartDisplayProps {
  data: PieChartSegment[];
  size?: number; // Diameter of the pie chart
  layout?: 'horizontal' | 'vertical';
  responsive?: boolean;
}

// Helper to calculate SVG arc coordinates
const getCoordinatesForPercent = (percent: number, radius: number, size: number) => {
    const x = size / 2 + radius * Math.cos(2 * Math.PI * percent);
    const y = size / 2 + radius * Math.sin(2 * Math.PI * percent);
    return [x, y];
};

export const PieChartDisplay: React.FC<PieChartDisplayProps> = ({ data, size = 180, layout = 'vertical', responsive = false }) => {
  if (!data || data.length === 0) {
    return <p className="text-center py-4 text-gray-500">Sem dados para o gráfico.</p>;
  }

  const radius = size / 2;
  let cumulativePercent = 0;
  const rotation = `rotate(-90 ${radius} ${radius})`;

  const slices = data.map((item, index) => {
    const [startX, startY] = getCoordinatesForPercent(cumulativePercent / 100, radius, size);
    cumulativePercent += item.percentage;
    const [endX, endY] = getCoordinatesForPercent(cumulativePercent / 100, radius, size);
    const largeArcFlag = item.percentage > 50 ? 1 : 0;
    const pathData = `M ${size/2},${size/2} L ${startX},${startY} A ${radius},${radius} 0 ${largeArcFlag} 1 ${endX},${endY} Z`;
    return <path key={index} d={pathData} fill={item.color} transform={rotation} />;
  });
  
  const layoutClasses = layout === 'horizontal' 
    ? responsive
      ? 'flex-col sm:flex-row gap-4 sm:gap-x-6 items-center justify-center'
      : 'flex-row gap-x-6 items-center'
    : 'flex-col gap-y-2 items-center';

  return (
    <div className={`flex ${layoutClasses}`}>
      <div>
        <svg 
          width={size} 
          height={size} 
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label="Gráfico de pizza representando distribuição de custos"
          className="max-w-full h-auto"
        >
          {slices}
        </svg>
      </div>
      <div>
        <ul className="space-y-1 text-xs">
          {data.map(item => (
            <li key={item.name} className="flex items-center gap-x-2 py-0">
              <span 
                className="w-2.5 h-2.5 block flex-shrink-0 rounded-sm"
                style={{ backgroundColor: item.color }}
              ></span>
              <span className="text-gray-700 dark:text-dark-text-secondary w-20 truncate" title={item.name}>{item.name}</span>
              <span className="font-semibold text-gray-900 dark:text-dark-text-primary whitespace-nowrap">
                {item.percentage.toFixed(1).replace('.',',')}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
